import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Calendar, ShoppingBag, CreditCard, 
  Tag, Ban, Edit, Clock, DollarSign, User, MapPin, Plus, X, Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatPrice, cn } from '@/lib/utils';

const segmentColors = {
  champions: 'bg-green-100 text-green-800 border-green-200',
  loyal: 'bg-blue-100 text-blue-800 border-blue-200',
  potential: 'bg-purple-100 text-purple-800 border-purple-200',
  at_risk: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  dormant: 'bg-gray-100 text-gray-800 border-gray-200',
  new: 'bg-teal-100 text-teal-800 border-teal-200'
};

const segmentDescriptions = {
  champions: 'High-value, frequent buyer with recent activity',
  loyal: 'Regular customer with consistent purchases',
  potential: 'Showing signs of becoming a loyal customer',
  at_risk: 'Previously active but engagement declining',
  dormant: 'No recent activity, may need re-engagement',
  new: 'Recently acquired customer'
};

export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/enterprise/customers/${customerId}`);
      setCustomer(res.data);
      setEditData({
        phone: res.data.phone || '',
        notes: res.data.notes || '',
        tags: res.data.tags || []
      });
    } catch (error) {
      toast.error('Failed to load customer');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/enterprise/customers/${customerId}`, editData);
      toast.success('Customer updated');
      setEditing(false);
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  const handleIssueCredit = async () => {
    if (!creditAmount || !creditReason) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await api.post(`/enterprise/customers/${customerId}/credit?amount=${creditAmount}&reason=${encodeURIComponent(creditReason)}`);
      toast.success(`Credit of ${formatPrice(parseFloat(creditAmount))} issued`);
      setShowCreditDialog(false);
      setCreditAmount('');
      setCreditReason('');
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to issue credit');
    }
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    if (editData.tags.includes(newTag.trim())) {
      toast.error('Tag already exists');
      return;
    }
    setEditData({
      ...editData,
      tags: [...editData.tags, newTag.trim()]
    });
    setNewTag('');
    setShowTagDialog(false);
  };

  const handleRemoveTag = (tag) => {
    setEditData({
      ...editData,
      tags: editData.tags.filter(t => t !== tag)
    });
  };

  const handleBanCustomer = async () => {
    if (!confirm('Are you sure you want to ban this customer?')) return;
    try {
      await api.put(`/enterprise/customers/${customerId}`, {
        is_banned: true,
        banned_reason: 'Banned by admin'
      });
      toast.success('Customer banned');
      fetchCustomer();
    } catch (error) {
      toast.error('Failed to ban customer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-16" data-testid="customer-detail-loading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="h-8 w-48 skeleton rounded mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="h-64 skeleton rounded" />
            </div>
            <div className="lg:col-span-2">
              <div className="h-96 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="min-h-screen pb-16" data-testid="customer-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/customers')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Customer Profile */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-stone-100 mx-auto flex items-center justify-center mb-4">
                    <User className="h-10 w-10 text-stone-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-stone-900">{customer.name}</h2>
                  <p className="text-stone-500">{customer.email}</p>
                  
                  {customer.is_banned && (
                    <Badge variant="destructive" className="mt-2">
                      Banned
                    </Badge>
                  )}
                  
                  <div className="mt-4">
                    <Badge className={cn("text-sm px-3 py-1", segmentColors[customer.rfm_segment])}>
                      {customer.rfm_segment?.replace('_', ' ').toUpperCase() || 'NEW'}
                    </Badge>
                    <p className="text-xs text-stone-500 mt-2">
                      {segmentDescriptions[customer.rfm_segment]}
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="h-4 w-4 text-stone-400" />
                    <span className="text-stone-600">{customer.email}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-stone-400" />
                    {editing ? (
                      <Input
                        value={editData.phone}
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                        placeholder="Add phone number"
                        className="h-8"
                      />
                    ) : (
                      <span className="text-stone-600">{customer.phone || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-stone-400" />
                    <span className="text-stone-600">
                      Customer since {new Date(customer.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                  {editing ? (
                    <>
                      <Button onClick={handleSave} className="w-full">
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setEditing(false)} className="w-full">
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setEditing(true)} className="w-full">
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Profile
                      </Button>
                      <Button variant="outline" onClick={() => setShowCreditDialog(true)} className="w-full">
                        <CreditCard className="h-4 w-4 mr-2" />
                        Issue Credit
                      </Button>
                      {!customer.is_banned && (
                        <Button variant="outline" className="w-full text-red-600 hover:text-red-700" onClick={handleBanCustomer}>
                          <Ban className="h-4 w-4 mr-2" />
                          Ban Customer
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Customer Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-stone-500">Total Orders</span>
                  <span className="font-semibold">{customer.order_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-500">Total Spent</span>
                  <span className="font-semibold">{formatPrice(customer.total_spent || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-500">Avg. Order Value</span>
                  <span className="font-semibold">{formatPrice(customer.average_order_value || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-500">Last Order</span>
                  <span className="text-stone-600">
                    {customer.last_order_at 
                      ? new Date(customer.last_order_at).toLocaleDateString()
                      : 'Never'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Tags Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  Tags
                  <Button variant="ghost" size="sm" onClick={() => setShowTagDialog(true)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(editing ? editData.tags : customer.tags)?.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      {editing && (
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-red-500" 
                          onClick={() => handleRemoveTag(tag)}
                        />
                      )}
                    </Badge>
                  ))}
                  {(!customer.tags || customer.tags.length === 0) && !editing && (
                    <span className="text-sm text-stone-500">No tags</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="orders" className="space-y-6">
              <TabsList>
                <TabsTrigger value="orders">Orders ({customer.orders?.length || 0})</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="addresses">Addresses</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
              </TabsList>

              <TabsContent value="orders">
                <Card>
                  <CardHeader>
                    <CardTitle>Order History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {customer.orders?.length > 0 ? (
                      <div className="space-y-4">
                        {customer.orders.map((order) => (
                          <Link 
                            key={order.order_id}
                            to={`/admin/orders`}
                            className="flex items-center justify-between p-4 rounded-lg border hover:bg-stone-50 transition"
                          >
                            <div className="flex items-center gap-4">
                              <ShoppingBag className="h-10 w-10 text-stone-300" />
                              <div>
                                <p className="font-medium text-stone-900">{order.order_id}</p>
                                <p className="text-sm text-stone-500">
                                  {new Date(order.created_at).toLocaleDateString()} • {order.items?.length || 0} items
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{formatPrice(order.total)}</p>
                              <Badge variant={order.status === 'delivered' ? 'success' : 'secondary'}>
                                {order.status}
                              </Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-stone-500">
                        <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                        <p>No orders yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="activity">
                <Card>
                  <CardHeader>
                    <CardTitle>Activity Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {customer.events?.length > 0 ? (
                      <div className="space-y-4">
                        {customer.events.map((event) => (
                          <div key={event.event_id} className="flex gap-4 p-3 rounded-lg bg-stone-50">
                            <Clock className="h-5 w-5 text-stone-400 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-stone-900">
                                {event.event_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                              </p>
                              <p className="text-xs text-stone-500">
                                {new Date(event.created_at).toLocaleString()}
                              </p>
                              {event.event_data && Object.keys(event.event_data).length > 0 && (
                                <p className="text-xs text-stone-600 mt-1">
                                  {JSON.stringify(event.event_data)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-stone-500">
                        <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                        <p>No activity recorded</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="addresses">
                <Card>
                  <CardHeader>
                    <CardTitle>Saved Addresses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {customer.addresses?.length > 0 ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        {customer.addresses.map((address) => (
                          <div key={address.address_id} className="p-4 rounded-lg border">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="h-4 w-4 text-stone-400" />
                              <span className="font-medium capitalize">{address.type}</span>
                              {address.is_default && (
                                <Badge variant="secondary" className="text-xs">Default</Badge>
                              )}
                            </div>
                            <p className="text-sm text-stone-600">
                              {address.first_name} {address.last_name}<br />
                              {address.address_line_1}<br />
                              {address.address_line_2 && <>{address.address_line_2}<br /></>}
                              {address.city}, {address.state} {address.postal_code}<br />
                              {address.country}
                            </p>
                            {address.phone && (
                              <p className="text-sm text-stone-500 mt-2">
                                <Phone className="h-3 w-3 inline mr-1" />
                                {address.phone}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-stone-500">
                        <MapPin className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                        <p>No saved addresses</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="notes">
                <Card>
                  <CardHeader>
                    <CardTitle>Internal Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {editing ? (
                      <Textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({...editData, notes: e.target.value})}
                        placeholder="Add notes about this customer..."
                        rows={6}
                      />
                    ) : (
                      <div className="min-h-[150px]">
                        {customer.notes ? (
                          <p className="text-stone-600 whitespace-pre-wrap">{customer.notes}</p>
                        ) : (
                          <p className="text-stone-400 italic">No notes added yet. Click "Edit Profile" to add notes.</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Issue Credit Dialog */}
      <Dialog open={showCreditDialog} onOpenChange={setShowCreditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Store Credit</DialogTitle>
            <DialogDescription>
              Add store credit to {customer.name}'s account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-stone-700">Amount (SGD)</label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                data-testid="credit-amount-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Reason</label>
              <Input
                placeholder="Reason for credit..."
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                data-testid="credit-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditDialog(false)}>Cancel</Button>
            <Button onClick={handleIssueCredit} data-testid="issue-credit-btn">Issue Credit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tag Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tag</DialogTitle>
            <DialogDescription>
              Add a tag to categorize this customer.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter tag name..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
              data-testid="new-tag-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTagDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTag} data-testid="add-tag-btn">Add Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
